import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import {
    fetchAirportCounters,
    confirmAirportClosing,
    createAirportClosing,
    deleteAirportClosing,
} from '@/pages/admin/top/api/airportClosing';
import { isPastTime } from '@/shared/utils/date';
import type { AirportCounters } from '@/pages/admin/top/model/types';

type AirportReception = AirportCounters[number]['airport_receptions'][number];

type AuthInfo = {
    isAirport: boolean;
    assignedCounterIds: readonly string[];
};

export const useClosingStatusStore = defineStore('closingStatus', () => {
    // State
    const airportCounters = ref<AirportCounters>([]);
    const selectedCounterId = ref<string>('');
    const isClosedByCounterId = ref<Map<string, boolean>>(new Map());
    const hasErrorByCounterId = ref<Map<string, boolean>>(new Map());
    const isStatusChecked = ref<boolean>(false);

    // Getters
    const selectedCounter = computed<AirportReception | null>(
        () =>
            airportCounters.value
                .flatMap((airport) => airport.airport_receptions)
                .find((r) => r.uuid === selectedCounterId.value) ?? null
    );

    const isPastClosingTime = computed<boolean>(() => {
        if (!selectedCounter.value) return false;
        return isPastTime(selectedCounter.value.reception_end_time);
    });

    const isClosingCompleted = computed<boolean>(() => isClosedByCounterId.value.get(selectedCounterId.value) ?? false);

    const unclosedCounters = computed<AirportReception[]>(() => {
        if (!isStatusChecked.value) return [];
        return airportCounters.value
            .flatMap((a) => a.airport_receptions)
            .filter((counter) => {
                const hasError = hasErrorByCounterId.value.get(counter.uuid) ?? false;
                if (hasError) return false;
                const isClosed = isClosedByCounterId.value.get(counter.uuid) ?? false;
                return isPastTime(counter.reception_end_time) && !isClosed;
            });
    });

    const canShowClosingAlert = computed<boolean>(() => unclosedCounters.value.length > 0);

    // Actions
    const filterByAssignedCounters = (data: AirportCounters, authInfo: AuthInfo): AirportCounters => {
        if (!authInfo.isAirport) return data;
        const assignedIds = authInfo.assignedCounterIds;
        if (assignedIds.length === 0) return data;
        return data
            .map((airport) => ({
                ...airport,
                airport_receptions: airport.airport_receptions.filter((reception) =>
                    assignedIds.includes(reception.uuid)
                ),
            }))
            .filter((airport) => airport.airport_receptions.length > 0);
    };

    const initializeCounters = async (authInfo: AuthInfo): Promise<void> => {
        const data = await fetchAirportCounters();
        const filtered = filterByAssignedCounters(data, authInfo);
        airportCounters.value = [...filtered];
        const [firstReception] = filtered.flatMap((a) => a.airport_receptions);
        selectedCounterId.value = firstReception?.uuid ?? '';
    };

    const checkClosingStatus = async (): Promise<void> => {
        const allCounters = airportCounters.value.flatMap((a) => a.airport_receptions);
        if (allCounters.length === 0) return;

        const results = await Promise.allSettled(allCounters.map((counter) => confirmAirportClosing(counter.uuid)));

        const newClosedMap = new Map<string, boolean>();
        const newErrorMap = new Map<string, boolean>();

        for (const [index, counter] of allCounters.entries()) {
            const result = results[index];
            if (result?.status === 'fulfilled') {
                newClosedMap.set(counter.uuid, result.value.is_already_closed);
                newErrorMap.set(counter.uuid, false);
            } else {
                newErrorMap.set(counter.uuid, true);
                // 一部カウンターのみ失敗した場合 UI に状態不明として表れないため、運用検知のため Sentry に送る
                Sentry.captureException(result?.reason, {
                    tags: { feature: 'closing-status-check' },
                    contexts: {
                        counter: { uuid: counter.uuid, japanese_name: counter.japanese_name },
                    },
                });
            }
        }

        isClosedByCounterId.value = newClosedMap;
        hasErrorByCounterId.value = newErrorMap;
        isStatusChecked.value = true;
    };

    const executeClosing = async (): Promise<void> => {
        if (!selectedCounterId.value) return;
        await createAirportClosing(selectedCounterId.value);
        isClosedByCounterId.value.set(selectedCounterId.value, true);
        // API成功で状態が確定するため、checkClosingStatusで残ったエラーフラグをクリアする
        hasErrorByCounterId.value.set(selectedCounterId.value, false);
    };

    const revertClosing = async (): Promise<void> => {
        if (!selectedCounterId.value) return;
        await deleteAirportClosing(selectedCounterId.value);
        isClosedByCounterId.value.set(selectedCounterId.value, false);
        // API成功で状態が確定するため、checkClosingStatusで残ったエラーフラグをクリアする
        hasErrorByCounterId.value.set(selectedCounterId.value, false);
    };

    const $reset = () => {
        airportCounters.value = [];
        selectedCounterId.value = '';
        isClosedByCounterId.value = new Map();
        hasErrorByCounterId.value = new Map();
        isStatusChecked.value = false;
    };

    return {
        // State
        airportCounters,
        selectedCounterId,
        isClosedByCounterId,
        hasErrorByCounterId,
        isStatusChecked,
        // Getters
        selectedCounter,
        isPastClosingTime,
        isClosingCompleted,
        unclosedCounters,
        canShowClosingAlert,
        // Actions
        initializeCounters,
        checkClosingStatus,
        executeClosing,
        revertClosing,
        $reset,
    };
});
